/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import styled from 'styled-components';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import mapContext from '../../../contexts/map-context';
import { useWindowSize } from '../../../hooks/useWindowSize';
import SearchResultEntry from './SearchResultsEntry';
import { Dealer } from '../../../models/dealer';
import trackingContext from '../../../contexts/tracking-context';
import {
  trackResultListDealerSelectionAction,
  trackDealerDetailsDisplayAction,
} from '../../../utils/trackingUtils';
import { getDistanceFromLatLonPoints } from '../../../utils/distance';
import { useURLService } from '../../../hooks/useURLService';
import { DISPLAY_SIZE } from '../../common/global.style.constants';

export interface Props {
  results: Dealer[] | null;
}

export default function SearchResultsList({ results }: Props) {
  const { selectedDealerId, hoveredDealer, userGeolocation, searchText, userSelectedFilters } =
    mapContext.useMapState();
  const dispatch = mapContext.useMapDispatch();
  const windowSize = useWindowSize();
  const generateURL = useURLService();
  const isMobile = windowSize.width < 768;
  const searchResultContainerRef = React.useRef<HTMLDivElement>(null);
  const hoveredResultRef = React.useRef<HTMLDivElement>(null);
  const { trackingService: tService } = trackingContext.useTrackingState();

  const handleDealerResultClick = async (dealer: Dealer, index: number) => {
    const currentUrl = window.location.href;
    const { dealerId, additionalData, kvpsId, country } = dealer;
    const name = additionalData?.displayName ?? dealer.name;
    const pos = index + 1;

    const payload = {
      query: searchText,
      filters: userSelectedFilters,
      'dealer-id': dealerId,
      selected: kvpsId,
      country,
    };
    const url = generateURL(currentUrl, payload);
    trackResultListDealerSelectionAction(
      tService,
      name,
      'SearchResultsList',
      searchText,
      pos,
      url.href,
    );

    dispatch({
      type: ActionTypes.setSelectedDealerId,
      payload: dealer.dealerId,
    });
    dispatch({
      type: ActionTypes.setSelectedPartner,
      payload: { kvpsid: dealer.kvpsId, tenant: dealer.country },
    });
    trackDealerDetailsDisplayAction(tService, dealer);
  };

  // eslint-disable-next-line
  const handleKeyPress = (event: any, dealer: Dealer, index: number) => {
    if (event.key && event.key === 'Enter') {
      handleDealerResultClick(dealer, index);
    }
    // eslint-disable-next-line
    const active = document.activeElement as any;
    if (event.key && event.key === 'ArrowDown') {
      active?.nextSibling?.focus();
    }
    if (event.key && event.key === 'ArrowUp') {
      active?.previousSibling?.focus();
    }
  };
  const sortByDistance = (a: Dealer, b: Dealer) =>
    userGeolocation
      ? getDistanceFromLatLonPoints(
          userGeolocation.lat,
          userGeolocation.lng,
          a.latitude,
          a.longitude,
        ) -
        getDistanceFromLatLonPoints(
          userGeolocation.lat,
          userGeolocation.lng,
          b.latitude,
          b.longitude,
        )
      : 0;

  const sorter = results?.every((dealer) => dealer.distance !== null) ? undefined : sortByDistance;

  return (
    <ScrollableListContainer
      data-test-id="search-results-list"
      onMouseLeave={() => {
        if (!isMobile) {
          dispatch({
            type: ActionTypes.setHoveredDealer,
            payload: null,
          });
        }
      }}
      ref={searchResultContainerRef}
    >
      {results
        ?.slice()
        ?.sort(sorter)
        ?.map((dealer, index) => {
          return (
            <div
              key={dealer.dealerId}
              ref={dealer.dealerId === hoveredDealer?.dealerId ? hoveredResultRef : undefined}
              onMouseEnter={() => {
                if (!isMobile) {
                  dispatch({
                    type: ActionTypes.setHoveredDealer,
                    payload: dealer,
                  });
                }
              }}
              onClick={() => handleDealerResultClick(dealer, index)}
              data-test-id="results-entry"
              onKeyDown={(e) => handleKeyPress(e, dealer, index)}
              tabIndex={0}
            >
              <SearchResultEntry
                dealer={dealer}
                isSelected={selectedDealerId === dealer.dealerId}
                isMapMarkerHovered={hoveredDealer?.dealerId === dealer.dealerId}
              />
            </div>
          );
        })}
    </ScrollableListContainer>
  );
}

const device = {
  desktop: `(min-width:${DISPLAY_SIZE.DESKTOP})`,
  largeDesktop: `(min-width:${DISPLAY_SIZE.LARGE_DESKTOP})`,
  smallDesktop: `(min-width:${DISPLAY_SIZE.SMALL_DESKTOP})`,
  mobile: `(max-width:${DISPLAY_SIZE.MOBILE})`,
  tablet: `(max-width: ${DISPLAY_SIZE.TABLET})`,
  smallTablet: `(min-width: 769px)`,
};

const ScrollableListContainer = styled.div`
  overflow-y: none;
  flex-grow: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-bottom: 0;
  &::-webkit-scrollbar {
    display: none;
  }

  @media ${device.smallTablet} {
    padding-bottom: 57px;
  }
`;
